/**
 * Session wall-clock display preference for the replay list and player (#30545).
 *
 * Stored in localStorage so list → player navigation keeps the same mode. Playback
 * offset clocks in devtools are intentionally out of scope — they are not session
 * timestamps.
 */

export type RynReplayTimezoneMode = 'local' | 'utc';

export const RYN_REPLAY_TIMEZONE_STORAGE_KEY = 'ryn-replay.timezone-mode.v1';

const SESSION_TIME_PARTS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

/** Wall clock within a session (tracked-event list): seconds matter, date is redundant. */
const EVENT_TIME_PARTS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

/**
 * Format an ISO-8601 UTC instant for the session list / tracked-event labels.
 * Returns '' when the input is not a parseable instant.
 */
export const formatRynReplaySessionTime = (
  isoUtc: string,
  mode: RynReplayTimezoneMode
): string => {
  const ms = Date.parse(isoUtc);
  if (!Number.isFinite(ms)) {
    return '';
  }

  const date = new Date(ms);
  if (mode === 'utc') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      ...SESSION_TIME_PARTS,
      timeZone: 'UTC',
    }).format(date);
    return `${formatted} UTC`;
  }

  return new Intl.DateTimeFormat('en-US', SESSION_TIME_PARTS).format(date);
};

/**
 * Format an event offset on the session timeline (epoch ms). Omits the date
 * because the player is always one session; includes seconds so nearby markers
 * do not collapse to the same label.
 */
export const formatRynReplayEventTime = (
  epochMs: number,
  mode: RynReplayTimezoneMode
): string => {
  if (!Number.isFinite(epochMs)) {
    return '';
  }

  const date = new Date(epochMs);
  if (mode === 'utc') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      ...EVENT_TIME_PARTS,
      timeZone: 'UTC',
    }).format(date);
    return `${formatted} UTC`;
  }

  return new Intl.DateTimeFormat('en-US', EVENT_TIME_PARTS).format(date);
};

/** Wall clock of the playhead: date + time with seconds. */
const PLAYBACK_TIME_PARTS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

/**
 * Format the recording wall clock at a playback offset (epoch ms). Includes
 * the date and seconds so the player header stays useful as the session is
 * scrubbed. Returns '' when the instant is not finite.
 */
export const formatRynReplayPlaybackTime = (
  epochMs: number,
  mode: RynReplayTimezoneMode
): string => {
  if (!Number.isFinite(epochMs) || epochMs <= 0) {
    return '';
  }

  const date = new Date(epochMs);
  if (mode === 'utc') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      ...PLAYBACK_TIME_PARTS,
      timeZone: 'UTC',
    }).format(date);
    return `${formatted} UTC`;
  }

  return new Intl.DateTimeFormat('en-US', PLAYBACK_TIME_PARTS).format(date);
};

/** Default is local time when nothing valid is stored. */
export const loadStoredTimezoneMode = (): RynReplayTimezoneMode => {
  try {
    const raw = localStorage.getItem(RYN_REPLAY_TIMEZONE_STORAGE_KEY);
    if (raw === 'utc' || raw === 'local') {
      return raw;
    }
  } catch {
    // SSR, locked-down profiles, or tests without storage.
  }
  return 'local';
};

export const persistTimezoneMode = (mode: RynReplayTimezoneMode): void => {
  try {
    localStorage.setItem(RYN_REPLAY_TIMEZONE_STORAGE_KEY, mode);
  } catch {
    // Quota or privacy mode — in-memory preference still works this visit.
  }
};

export const isUtcTimezoneMode = (mode: RynReplayTimezoneMode): boolean =>
  mode === 'utc';

/**
 * The browser's IANA timezone (e.g. `America/Chicago`), from `Intl`. Used to
 * label local-mode timestamps so "local" is not ambiguous next to explicit UTC.
 */
export const resolveUserTimezoneId = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    if (tz) {
      return tz;
    }
  } catch {
    // Environments without Intl stay generic.
  }
  return 'local';
};
